import { Injectable, inject } from '@angular/core';
import { Observable, of, tap } from 'rxjs';

import { IPlanOption, ISelfPayRate, IStateData } from '@sondermind/cost-estimate-core';

import { CostEstimateHttpService } from './cost-estimate.httpservice';

/**
 * Caches cost-estimate reference pricing per state and per plan id. Reference pricing doesn't change
 * during a session, and one state's payload covers both services, so UT → CO → UT is one fetch each.
 *
 * Turning a payload into a dollar figure is `@sondermind/cost-estimate-core`'s job — import
 * `getPlansForService` / `getInsuranceEstimate` / `getSelfPayEstimate` / `formatUsd` where you render.
 */
@Injectable({
  providedIn: 'root'
})
export class CostEstimateStore {
  private readonly http = inject(CostEstimateHttpService);

  private readonly stateData = new Map<string, IStateData>();
  private readonly selfPayData = new Map<string, ISelfPayRate | null>();
  private readonly plans = new Map<number, IPlanOption>();

  getStateData$(stateAbbreviation: string): Observable<IStateData> {
    const cached = this.stateData.get(stateAbbreviation);
    if (cached) return of(cached);

    return this.http.getStateData$(stateAbbreviation).pipe(
      // Only on success — a failed request must not cache an empty payload as "no plans here".
      tap((data) => this.stateData.set(stateAbbreviation, data))
    );
  }

  getSelfPayData$(stateAbbreviation: string): Observable<ISelfPayRate | null> {
    if (this.selfPayData.has(stateAbbreviation)) return of(this.selfPayData.get(stateAbbreviation) ?? null);

    return this.http.getSelfPayData$(stateAbbreviation).pipe(
      tap((data) => this.selfPayData.set(stateAbbreviation, data))
    );
  }

  getPlanById$(id: number): Observable<IPlanOption> {
    const cached = this.plans.get(id);
    if (cached) return of(cached);

    return this.http.getPlanById$(id).pipe(tap((plan) => this.plans.set(id, plan)));
  }
}
